import {
  RentalVehicleSummary,
  type RentalStopView,
  type RentalVehicleView,
} from '@voll-intelligence/intelligence-ui-kit';
import { useOfferLayout } from '@voll-intelligence/intelligence-ui-kit';
import { rentalVehicleCopy } from '../../../../rental/shared/rental_vehicle_copy.js';
import { useGenUiI18n, type GenUiTranslate } from '../../../../shared/ui/i18n.js';
import type { CheckoutItem } from '@voll-intelligence/genui-contract';
import { formatCompactDateTime } from '@voll-intelligence/intelligence-ui-kit/checkout';

export type RentalCheckoutItem = Extract<CheckoutItem, { productType: 'rental' }>;

/** «Onix Plus ou similar»: a foto e o modelo são da CATEGORIA, nunca promessa —
 *  sem duplicar quando o fornecedor já escreve «ou similar» no nome. Sem modelo
 *  (o nome é a própria categoria), não há «ou similar» a mostrar. */
function similarModel(item: RentalCheckoutItem, t: GenUiTranslate): string | undefined {
  const { vehicleName: name, category } = item.detail;
  if (!name || name.trim().toLowerCase() === category.trim().toLowerCase()) return undefined;
  return /similar/i.test(name) ? name : t('productCheckout.rental.orSimilar', { model: name });
}

/** «pelas 3 diárias»: a nota sob o total do rodapé. */
export function rentalDaysNote(item: RentalCheckoutItem, t: GenUiTranslate): string | undefined {
  const { days } = item.detail;
  if (!days) return undefined;
  const key =
    days === 1 ? 'productCheckout.rental.daysNote_one' : 'productCheckout.rental.daysNote_other';
  return t(key, { count: days });
}

function stopOf(
  agency: string | undefined,
  address: string | undefined,
  at: string | undefined,
  locale: string,
): RentalStopView {
  return {
    agencyLabel: agency || address || '',
    ...(agency && address ? { addressLabel: address } : {}),
    ...(at ? { whenLabel: formatCompactDateTime(at, locale) } : {}),
  };
}

/** O veículo do item na forma do card da listagem; a devolução sem local é a
 *  própria retirada. */
function vehicleOf(item: RentalCheckoutItem, t: GenUiTranslate, locale: string): RentalVehicleView {
  const { detail } = item;
  const model = similarModel(item, t);
  // A devolução com local próprio (agência OU só endereço) nunca herda a
  // agência da retirada: herdar a fundiria com ela e apagaria o endereço.
  const ownDropoff = Boolean(detail.dropOffAgency || detail.dropOffAddress);
  return {
    categoryLabel: detail.category,
    ...(model ? { vehicleLabel: model } : {}),
    ...(detail.thumbUrl ? { thumbnailUrl: detail.thumbUrl } : {}),
    company: {
      name: detail.rentalCompany,
      ...(detail.companyLogoUrl ? { logoUrl: detail.companyLogoUrl } : {}),
    },
    transmission: detail.transmission === 'AUTOMATIC' ? 'automatic' : 'manual',
    airConditioning: detail.airConditioning,
    pickup: stopOf(detail.pickUpAgency, detail.pickUpAddress, detail.pickupAt, locale),
    dropoff: ownDropoff
      ? stopOf(detail.dropOffAgency, detail.dropOffAddress, detail.dropoffAt, locale)
      : stopOf(detail.pickUpAgency, detail.pickUpAddress, detail.dropoffAt, locale),
  };
}

/**
 * O bloco «Contrato» do carro no card 1click: o miolo do card da listagem
 * (`RentalVehicleSummary` do kit) — locadora e foto, categoria · modelo, câmbio
 * e ar, retirada e devolução — sem selo, preço nem nota, e com a data e a hora
 * sob cada local, porque aqui é o contrato. O preço e as diárias ficam no
 * rodapé do card. O desenho segue a régua `sm` medida no próprio bloco, como
 * o card de voo: a linha no largo, o card v2 no estreito.
 */
export function RentalCheckoutContract({ item }: { readonly item: RentalCheckoutItem }) {
  const { t, locale } = useGenUiI18n();
  const { ref, layout } = useOfferLayout<HTMLDivElement>();
  return (
    <div ref={ref}>
      <RentalVehicleSummary
        vehicle={vehicleOf(item, t, locale)}
        copy={rentalVehicleCopy(t)}
        layout={layout}
      />
    </div>
  );
}
